import * as React from "react"
import { useAuth } from "../../../../../App";
import { OrthodonticReferralDecisionApi } from "../../../../../api/OrthodonticReferralDecisionApi";
import { useHistory } from "react-router";
import { IOrthodonticReferralDecision, IOrthodonticReferralConditionCode, IOrthodonticReferralTreatmentNotNeededReason, IOrthodonticReferralTreatmentDecision, IOrthodonticReferral } from "../../../../../models/IOrthodonticReferral";
import { IStandardPhrase } from "../../../../../models/IStandardPhrase";
import { UserContext, isInRole } from "../../../../../contexts/UserContext";
import { SAMRÅDSGRUPP } from "../../../../../utils/RoleConstants";
import { ORTHODONTIC_REFERRAL_PROVNINGSKONSULT } from "../../../../../utils/RouteConstants";
import { setMessage } from "../../../../../helpers/MessageHelper";
import { Modal, ModalSize, ModalBody, ModalFooter, AlertMessage, CustomButton, ButtonType, AlertType } from "soprasteria-ui-library";
import { OrthodonticReferralTreatmentDecision } from "./OrthodonticReferralTreatmentDecision";
import { OrthodonticReferralProvningskonsultPrompt } from "./OrthodonticReferralProvningskonsultPrompt";
import { IPatient } from "../../../../../models/IPatient";
import { PatientOrthodonticReferralnformation } from "./PatientOrthodonticReferralnformation";

interface IOrthodonticReferralTreatmentDecisionModal {
    orthodonticReferral: IOrthodonticReferral;
    patient: IPatient
    changeSuccessMessage: (message: string) => void;
    getOrthodonticReferral: (id: number) => void;
    awaitingDecisionMotivationText?: string;
    renderForm: boolean;
    setRenderForm: React.Dispatch<React.SetStateAction<boolean>>;
}


export const OrthodonticReferralTreatmentDecisionModal: React.FunctionComponent<IOrthodonticReferralTreatmentDecisionModal> = ({
    orthodonticReferral,
    patient,
    changeSuccessMessage,
    getOrthodonticReferral,
    awaitingDecisionMotivationText,
    setRenderForm,
    renderForm }) => {
    const auth = useAuth();
    const api = new OrthodonticReferralDecisionApi(auth);
    const history = useHistory();
    const abortController = new AbortController();
    const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>();
    const [successMessage, setSuccessMessage] = React.useState<string>();
    const [decisions, setDecisions] = React.useState<IOrthodonticReferralDecision[]>();
    const [conditionCodes, setConditionCodes] = React.useState<IOrthodonticReferralConditionCode[]>();
    const [treatmentNotNeededReasons, setTreatmentNotNeededReasons] = React.useState<IOrthodonticReferralTreatmentNotNeededReason[]>();
    const [standardPhrases, setStandardPhrases] = React.useState<IStandardPhrase[]>();
    const [showStandardPhraseTable, setShowStandardPhraseTable] = React.useState<boolean>(true);
    const userInfo = React.useContext(UserContext);
    const hasRoleProvningskonsult = isInRole(SAMRÅDSGRUPP, userInfo.roles ? userInfo.roles : []);
    const [renderPrompt, setRenderPrompt] = React.useState<boolean>(false);
    const [submittedDecision, setSubmittedDecision] = React.useState<IOrthodonticReferralTreatmentDecision>();
    const orthodonticReferralId = orthodonticReferral?.orthodonticReferralId ?? 0;

    React.useEffect(() => {
        getOrthodonticReferralDecisions();
        getOrthodonticReferralTreatmentNotNeededReasons();
        getOrthodonticReferralConditionCodes();
        getStandardPhrases();

        return () => {
            abortController.abort();
        }
    }, [])

    React.useEffect(() => {
        if (!renderForm) {
            setRenderPrompt(false);
            setSubmittedDecision(undefined);
        }

        return () => {
            abortController.abort();
        }
    }, [renderForm])

    const onFormSave = (data: IOrthodonticReferralTreatmentDecision) => {
        saveTreatmentDecisionMotivationText(data);
    }

    const onFormSubmit = (data: IOrthodonticReferralTreatmentDecision) => {
        if (hasRoleProvningskonsult) {
            setSubmittedDecision(data);
            setRenderPrompt(true);
        }
        else {
            sendTreatmentDecision(data);
        }
    }

    const saveTreatmentDecisionMotivationText = (data: IOrthodonticReferralTreatmentDecision | undefined) => {
        if (data) {
            setIsSubmitting(true);
            api.saveTreatmentDecisionMotivationText(data, () => {
                const message = "Motivering till beslut har sparats";
                document.getElementById("close-orthodontic-referral-treatment-decision-modal")?.click();
                getOrthodonticReferral(orthodonticReferralId);
                changeSuccessMessage(message);                
            }, (error) => {
                changeErrorMessage(error);
            }, () => {
                setIsSubmitting(false);
            });
        }
    }

    const sendTreatmentDecision = (data: IOrthodonticReferralTreatmentDecision | undefined) => {
        if (data) {
            setIsSubmitting(true);
            api.sendTreatmentDecision(data, () => {
                const message = "Beslut om vård har skickats";
                document.getElementById("close-orthodontic-referral-treatment-decision-modal")?.click();
                if (hasRoleProvningskonsult) {
                    history.replace({ pathname: `/${ORTHODONTIC_REFERRAL_PROVNINGSKONSULT}`, state: message });
                }
                else {
                    getOrthodonticReferral(orthodonticReferralId);
                    changeSuccessMessage(message);
                }
            }, (error) => {
                changeErrorMessage(error);
            }, () => {
                setIsSubmitting(false);
            });
        }
    }

    const getOrthodonticReferralDecisions = () => {
        api.getOrthodonticReferralDecisions(orthodonticReferralId, (data) => {
            setDecisions(data);
        });
    }

    const getOrthodonticReferralTreatmentNotNeededReasons = () => {
        api.getOrthodonticReferralTreatmentNotNeededReasons((data) => {
            setTreatmentNotNeededReasons(data);
        });
    }

    const getOrthodonticReferralConditionCodes = () => {
        api.getOrthodonticReferralConditionCodes((data) => {
            setConditionCodes(data);
        });
    }

    const getStandardPhrases = () => {
        api.getOrthodonticReferralDecisionStandardPhrases((data) => setStandardPhrases(data));
    }

    const getSubmittedDecisionDescription = (): string | undefined => {
        if (submittedDecision) {
            return decisions?.find(x => x.orthodonticReferralDecisionId.toString() === submittedDecision.orthodonticReferralDecisionId.toString())?.description;
        }
    }

    const changeErrorMessage = (message: string) => {
        var newMessage = setMessage(message, errorMessage ?? " ")
        setErrorMessage(newMessage);
    }

    const changeSuccessModalMessage = (message: string) => {
        var newMessage = setMessage(message, successMessage ?? " ")
        setSuccessMessage(newMessage);
    }

    return (
        <Modal id="orthodontic-referral-treatment-decision-modal"
            header={"Fatta beslut om vård"}
            modalSize={showStandardPhraseTable ? ModalSize.extraLarge : ModalSize.normal}>
            <ModalBody>
                <AlertMessage alertType={AlertType.danger} duration={3000}>{errorMessage}</AlertMessage>
                <AlertMessage alertType={AlertType.success} duration={3000}>{successMessage}</AlertMessage>
                {
                    renderForm ?
                        <>
                            {
                                showStandardPhraseTable
                                    ? <div>
                                          <PatientOrthodonticReferralnformation orthodonticReferral={orthodonticReferral} patient={patient} />
                                          <hr />
                                      </div>
                                    : null
                            }
                            {
                                renderPrompt
                                    ? <OrthodonticReferralProvningskonsultPrompt selectedDecision={getSubmittedDecisionDescription()} motivation={submittedDecision?.text} />
                                    : <OrthodonticReferralTreatmentDecision
                                        orthodonticReferralId={orthodonticReferralId}
                                        standardPhrases={standardPhrases}
                                        decisions={decisions}
                                        conditionCodes={conditionCodes}
                                        treatmentNotNeededReasons={treatmentNotNeededReasons}
                                        getStandardPhrases={getStandardPhrases}
                                        awaitingDecisionMotivationText={awaitingDecisionMotivationText}
                                        onFormSave={onFormSave}
                                        onFormSubmit={onFormSubmit}
                                        isSendingDecision={isSubmitting}
                                        showStandardPhraseTable={showStandardPhraseTable}
                                        setShowStandardPhraseTable={setShowStandardPhraseTable}
                                        changeSuccessMessage={changeSuccessModalMessage}
                                        changeErrorMessage={changeErrorMessage} />
                            }
                        </>
                        : null
                }
            </ModalBody>
            <ModalFooter>
                <span className={`${showStandardPhraseTable ? "" : "d-none"}`}>
                    <CustomButton buttonType={ButtonType.general} buttonText="Avbryt" data-dismiss="modal" id="close-orthodontic-referral-treatment-decision-modal" onClick={() => setRenderForm(false)} />
                    {
                        renderPrompt
                            ? <CustomButton buttonType={ButtonType.createAlt} buttonText="Ja" onClick={() => sendTreatmentDecision(submittedDecision)} />
                            : null
                    }
                </span>
            </ModalFooter>
        </Modal>
    );
}